/**
 * @summary - Component that Provides PPM CheckList Creation Functionality
 * @author vengateshwaran k
 * @since 2024-08-14
 */
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from "@mui/material";
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useNavigate } from 'react-router-dom';
import commonMasterRepository from '../../api/repositories/commonMasterRepository';
import PPMCheckListRepository from "../../api/repositories/ppmChecklistRepository";
import PremitiveMaintenanceRepository from "../../api/repositories/premitiveMaintenanceRepository";
import { DefaultCard } from '../../assets/styled-components/card';
import { BemsGraph, FemsGraph, MasterGraph } from '../../utility/apiURLS';
import {
  getAuthToken,
  getFacilityId,
  getcompanyId
} from "../../utility/tokenUtility";
import utilities from "../../utility/utilities";
import ActionButtons from "../commoncomponents/ActionButtons";
import CustomDialog from '../commoncomponents/CustomDialog';
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomTextField from '../commoncomponents/CustomTextField';
import CustomTextFieldWithSearch from "../commoncomponents/CustomTextFieldWithSearch";
import PageTitle from "../commoncomponents/PageTitle";
import FileHandling from "./FileHandling";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
const initialRow = {
  id: '',
  file: null,
  fileName: '',
  fileType: '',
  size: '',
};
export default function CreatePpmCheckList({ isViewMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const childRef = useRef(null);
  const [id, setId] = useState("");
  const [fRowData, setFRowData] = useState([]);
  const [service, setService] = useState(); // Initialized as null
  const [serviceOptions, setServiceOptions] = useState([]);
  const [typeCode, setTypeCode] = useState("");
  const [typeCodeOptions, setTypeCodeOptions] = useState([]);
  const [typeCodeDescription, setTypeCodeDescription] = useState("");
  const [taskCode, setTaskCode] = useState("");
  const [taskCodeOptions, setTaskCodeOptions] = useState([]);
  const [assetClassificationOptions, setAssetClassificationOptions] = useState([]);
  const [assetClassification, setAssetClassification] = useState((location?.state?.mode === 'edit' || location?.state?.mode === 'view') ? assetClassificationOptions?.find((item) => item?.label === location?.state?.MasassetclassificationId) : assetClassificationOptions?.find((item) => item?.label === "Asset"));
  const [taskCodeDescription, setTaskCodeDescription] = useState("");
  const statusOptions = [{ label: "Active", id: 1, value: true, default: true }, { label: "In Active", id: 2, value: false, default: false }];
  const [status, setStatus] = useState((location?.state?.mode === 'edit' || location?.state?.mode === 'view') ? statusOptions?.find((item) => item?.default === location?.state?.isactive) : statusOptions?.find((item) => item?.default === true));
  const [enableSubmit, setEnableSubmit] = useState(true);
  const [isChanged, setIsChanged] = useState(false);
  const [isAddNew, setIsAddNew] = useState(false);
  const [contentDialog, setContentDialog] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [selectedCombination, setSelectedCombination] = useState(false);
  const [openDialogForSelectedCombination, setOpenDialogForSelectedCombination] = useState(false);
  const [existImageName, setExistImageName] = useState();
  const [existRow, setExistRow] = useState([]);
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const [ppmChecklistNo, setPPMChecklistNo] = useState();
  const [ppmChecklistNoField, setPpmChecklistNoField] = useState();
  const [isDataUpdatedByChangeAttachement, setDataUpdatedByChangeAttachement] = useState(false);
  const [workGroup, setWorkGroup] = useState(null);
  const [workGroupOptions, setWorkGroupOptions] = useState([]);
  const [errors, setErrors] = useState({
    service: false,
    taskCode: false,
    typeCode: false,
    status: false,
    assetClassification: false
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toAvoidDuplicateRecordInCreateMode, setToAvoidDuplicateRecordInCreateMode] = useState(false);
  const workGroupID = useRef('');
  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId("companyId");
  const facilityId = getFacilityId("facilityId");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [gridAPI, setGridAPI] = useState({});
  const setEmpty = async () => {
    setTaskCode({ label: '', value: '' });
    setTypeCode({ label: '', value: '' });
    setTaskCodeDescription('');
    setTypeCodeDescription('');
  }
  const handleTaskCodeClick = (newValue) => {
    setTaskCode(newValue?.value !== '' ? { ...newValue, label: newValue.value } : '');
    setTaskCodeDescription(newValue?.taskdesc);
    setToAvoidDuplicateRecordInCreateMode(false);
    if (newValue?.value === '') {
      setTaskCodeDescription('');
    }
    if (newValue?.value !== '') {
      setErrors((prevErrors) => ({ ...prevErrors, taskCode: "" }));
      setIsChanged(true);
    }

    // Close the modal after asset selection
    handleCloseModal();
  };

  const columnsTaskCode = [
    {
      headerName: 'Task Code',
      field: 'label',
      width: 150,
      cellRenderer: (params) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleTaskCodeClick(params.data);
          }}
          style={{
            color: 'blue',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {params.value}
        </a>
      ),
    },
    { headerName: 'Task Description', field: 'taskdesc', width: 200 },
    { headerName: 'Service', field: 'serviceName', width: 100, valueGetter: () => `${service?.label}`, },
    { headerName: 'Work Group & Work Group Description', field: 'wgCode', width: 150, valueGetter: () => `${workGroup?.label}-${workGroup.wgdesc}` },
  ];
  const setDefault = async () => {
    await setEmpty();
    setPpmChecklistNoField('');
    setService(serviceOptions?.find((item) => item?.label === "FEMS"));
    setStatus(statusOptions?.find((item) => item?.default === true))
    setAssetClassification(assetClassificationOptions?.find((item) => item?.label === "Asset"))
    setFRowData([initialRow]);
    setErrors((prevErrors) => ({ ...prevErrors, assetClassification: "", status: "", service: "", taskCode: "", typeCode: "", workGroup: "" }));
  }

  useEffect(() => {
    if (selectedFiles?.length > 0) {
      setIsChanged(true);
    }
  }, [selectedFiles]);

  const getTaskAndTypeCode = async () => {
    let ppmCheckList;
    if (location?.state?.service === 'FEMS' || service?.label === 'FEMS') {
      ppmCheckList = await PPMCheckListRepository.getPPMChecklistListForExport(facilityId);
    } else {
      ppmCheckList = await PPMCheckListRepository.getBemsPPMChecklistListForExport(facilityId);
    }
    if (typeCode?.label != '' && taskCode?.label != '') {
      if (location.state?.mode === "create") {
        const rowList = ppmCheckList?.data?.filter((item) => {
          if ((item?.taskcode === taskCode?.label) && (item?.typecode === typeCode?.label)) {
            return item;
          }
        })
        if (rowList?.length > 0) {
          setSelectedCombination(true);
          setExistImageName(rowList[0]?.documenttitle);
          setExistRow(rowList[0]);

        } else {
          setSelectedCombination(false);
          setFRowData([initialRow]);
        }
      } else if (location.state?.mode === "edit") {
        const rowList = ppmCheckList?.data?.filter((item) => {
          if ((item?.taskcode === taskCode?.label) && (item?.typecode === typeCode?.label)) {
            return item;
          }
        })

        if (rowList?.length > 0) {
          setSelectedCombination(true);
          setExistImageName(rowList[0]?.documenttitle);
          setExistRow(rowList[0]);
          if ((location?.state?.typecode != typeCode?.label) && (location?.state?.femtaskdetailsId != taskCode?.id)) {
            setDataUpdatedByChangeAttachement(true);
          }

          if (isChanged) {
            setOpenDialogForSelectedCombination(true);
          }

        } else {
          setSelectedCombination(false);
          setFRowData([initialRow]);
        }
      }
    }
  }
  const combinationCheck = useMemo(() => {
    if (!!typeCode && !!taskCode) {
      getTaskAndTypeCode();
    }


  }, [typeCode, taskCode])

  const fetchServiceAndTaskCode = async () => {
    try {
     
      const responseMasters = await commonMasterRepository.getMasters(facilityId, companyId);
     
  
      setServiceOptions(responseMasters);
      setService(responseMasters?.find((item) => item.label === location?.state?.service));
  
      if (location.state.mode !== "create") {
      
        let workGroupOptions = location.state.service === "FEMS"
          ? await commonMasterRepository.getWorkGroupDetails(facilityId)
          : await commonMasterRepository.getBemsWorkGroupDetails(facilityId);
  
      
        setWorkGroupOptions(workGroupOptions);
  
    
        let taskCodeOption = location.state?.service === "FEMS"
          ? await PremitiveMaintenanceRepository.getTaskCodeDetails(facilityId, location.state?.femworkgroupdetailsId)
          : await PremitiveMaintenanceRepository.getBemsTaskCodeDetails(facilityId, location.state?.bemworkgroupdetailsId);
  
      
        // Check if taskCodeOption is empty
        if (!taskCodeOption || taskCodeOption.length === 0) {
          console.error("Error: Task Code options are empty.");
          throw new Error("Task Code options are empty.");
        }
  
        let obj = taskCodeOption.find((item) => item.label === location.state.taskcode);
  
        // If the task code is not found, log available options
        if (!obj) {
          console.error("Error: Task Code not found. Available options:", taskCodeOption.map((item) => item.label));
          throw new Error("Task Code not found in options.");
        }
  
        setTaskCode(obj);
  
        let getWorkGroup = location.state?.service === "FEMS"
          ? workGroupOptions?.find((item) => item.id === obj.femworkgroupdetailsId)
          : workGroupOptions?.find((item) => item.id === obj.bemworkgroupdetailsId);
  
       
        setWorkGroup(getWorkGroup);
      }
  
      if (location.state.mode === "create") {
       
        const ppmChecklistNo = await PPMCheckListRepository.getPPMChecklistNo(facilityId, token);
      
        setPPMChecklistNo(ppmChecklistNo);
      } else {
       
        if (location?.state.service === "BEMS") {
          setPpmChecklistNoField(location?.state?.bemppmchecklistno);
        } else if (location?.state.service === "FEMS") {
          setPpmChecklistNoField(location?.state?.femppmchecklistno);
        }
      }
    } catch (e) {
      console.error("Error fetching resources:", e);
    }
  };
  
  

  useEffect(() => {
    fetchServiceAndTaskCode();
  }, []);

  const workGroupChange = useMemo(() => {
    const fetchTaskCode = async () => {
      try {
        let taskCodeOption = service?.label === "FEMS" ? await PremitiveMaintenanceRepository.getTaskCodeDetails(
          facilityId,
          workGroup?.id
        ) : await PremitiveMaintenanceRepository.getBemsTaskCodeDetails(
          facilityId,
          workGroup?.id
        );
        setTaskCodeOptions(taskCodeOption);
      }
      catch (e) {
        console.error(e);
      }
    }
    if (workGroup?.label != undefined) {
      fetchTaskCode();
    }

  }, [workGroup])


  const serviceChanges = useMemo(() => {
    const serviceAndTaskCode = async () => {
      try {
        if (service?.label != undefined) {
          let workGroupOptions = service?.label === "FEMS" ? await commonMasterRepository.getWorkGroupDetails(facilityId) : await commonMasterRepository.getBemsWorkGroupDetails(facilityId);
          setWorkGroupOptions(workGroupOptions);

          if (workGroupOptions?.length > 0 && location.state.mode === 'create') {
            if (service?.label === "FEMS") {
              setWorkGroup(workGroupOptions?.find((item) => item?.label === 'W1'))
            } else if (service?.label === "BEMS") {
              setWorkGroup(workGroupOptions?.find((item) => item?.label === 'W8'))
            }
          }
        }
        setTypeCode({ label: '', value: '' });
        setTypeCodeDescription('');
        setTaskCode({ label: '', value: '' });
        setTaskCodeDescription('');
        setStatus(statusOptions?.find((item) => item?.default === true))
        if (location?.state?.mode === "create") {
          setFRowData([initialRow]);
        }
        let assetClassificationOptions;
        if (location.state.service === 'FEMS' || service?.label === "FEMS") {
          assetClassificationOptions = await PPMCheckListRepository.getAssetClassification();
        }
        else if (location.state.service === 'BEMS' || service?.label === "BEMS") {
          assetClassificationOptions = await PPMCheckListRepository.getBemsAssetClassification();
        }
        if (location.state.service === 'FEMS' || service?.label === "FEMS") {
          setAssetClassificationOptions(assetClassificationOptions?.filter((item) => item?.label != "Doctor`s Asset"));
        } else if (location.state.service === 'BEMS' || service?.label === "BEMS") {
          setAssetClassificationOptions(assetClassificationOptions);
        }

        setAssetClassification(assetClassificationOptions?.find((item) => item?.label === "Asset"));
        setErrors((prevErrors) => ({ ...prevErrors, assetClassification: "", status: "" }));
        setIsChanged(false);

      } catch (e) {
        console.log("error fetching resources", e);
      }
    }
    serviceAndTaskCode();

  }, [service])


  const assetService = useMemo(() => {
    const getTypeCodes = async () => {
      try {
        let typeCodeOptions;
        if (assetClassification?.label != '' && assetClassification?.label != undefined) {
          let assetClassificationId = location.state.mode != "create" ? location?.state?.MasassetclassificationId : assetClassification?.id;
          if (location.state.mode != "create") {
            typeCodeOptions = location.state.service == "FEMS" ? await PPMCheckListRepository.getTypeCode(companyId, "2f702577-5b94-4975-9367-e1b3721f6e7a", location?.state?.masassetclassificationId) : await PPMCheckListRepository.getTypeCode(companyId, "a9a74b9a-2319-4ee3-861d-005e00938b22", location?.state?.masassetclassificationId);

          } else {
            typeCodeOptions = service?.label == "FEMS" ? await PPMCheckListRepository.getTypeCode(companyId, "2f702577-5b94-4975-9367-e1b3721f6e7a", assetClassification?.id) : await PPMCheckListRepository.getTypeCode(companyId, "a9a74b9a-2319-4ee3-861d-005e00938b22", assetClassification?.id);

          }
          setTypeCodeOptions(typeCodeOptions);
        }
      }
      catch (e) {
        console.error(e)
      }

    }
    getTypeCodes();
  }, [assetClassification])

  useEffect(() => {
    if (taskCodeOptions?.length > 0 && typeCodeOptions?.length > 0 && serviceOptions?.length > 0 && (location.state.mode === "edit" || location.state.mode === "view")) {
      setEdit();

    }
  }, [taskCodeOptions, typeCodeOptions, serviceOptions, location.state])

  const handleTaskCodeChange = (newValue) => {
    setTaskCode(newValue?.value !== '' ? { ...newValue, label: newValue.value } : '');
    setTaskCodeDescription(newValue?.taskdesc);
    setToAvoidDuplicateRecordInCreateMode(false);
    if (newValue?.value === '') {
      setTaskCodeDescription('');
    }
    if (newValue?.value !== '') {
      setErrors((prevErrors) => ({ ...prevErrors, taskCode: "" }));
      setIsChanged(true);
    }
  };
  const handleTypeCodeChange = (newValue) => {
    setTypeCode(newValue?.value !== '' ? { ...newValue, label: newValue.value } : '');
    setTypeCodeDescription(newValue?.typedescription);
    setToAvoidDuplicateRecordInCreateMode(false);
    if (newValue?.value === '') {
      setTypeCodeDescription('');
    }
    if (newValue?.value !== '') {
      setErrors((prevErrors) => ({ ...prevErrors, typeCode: "" }));
      setIsChanged(true);
    }
  };
  const handleAddNew = async () => {
    if (isChanged) {
      setIsAddNew(true);
      setCancelDialogOpen(true);
    } else {
      await setDefault();
    }
  };

  const setAssetTypeCodeField = async (typecode) => {
    let typeCode = typeCodeOptions?.find((item) => item.label === typecode)
    setTypeCode(typeCode);

  }
  const setAssetTaskCodeField = async (id) => {
    let taskCode = taskCodeOptions?.find((item) => item.id === id)
    setTaskCode(taskCode);

  }

  const setEdit = async () => {
    console.log("Location State:", location.state); // Check the incoming state data
  
    if (location.state?.mode === "edit" || location.state?.mode === "view") {
      if (location?.state?.service === "FEMS") {
        const {
          masservicedetailsId,
          taskcode,
          taskdesc,
          typeDescription,
          typecode,
          isactive,
          femppmchecklistno,
          femtaskdetailsId,
          filename,
          filepath,
          filesize,
          masassettypecodeId,
          readOnlyEdit,
          mode,
          service,
          masassetclassificationId
        } = location.state;
  
        console.log("FEMS Data:", {
          masservicedetailsId,
          taskcode,
          taskdesc,
          typeDescription,
          typecode,
          isactive,
          femtaskdetailsId,
          masassetclassificationId
        });
  
        await setAssetTypeCodeField(typecode);
        setTypeCodeDescription(typeDescription);
        await setAssetTaskCodeField(femtaskdetailsId);
        setTaskCodeDescription(taskdesc);
        setStatus(statusOptions?.find((item) => item?.default === isactive));
        setAssetClassification(
          assetClassificationOptions?.find((item) => item.id === masassetclassificationId)
        );
      } else {
        const {
          masservicedetailsId,
          taskcode,
          taskdesc,
          typeDescription,
          typecode,
          isactive,
          bemppmchecklistno,
          bemtaskdetailsId,
          filename,
          filepath,
          filesize,
          masassettypecodeId,
          readOnlyEdit,
          mode,
          service,
          masassetclassificationId
        } = location.state;
  
        console.log("BEMS Data:", {
          masservicedetailsId,
          taskcode,
          taskdesc,
          typeDescription,
          typecode,
          isactive,
          bemtaskdetailsId,
          masassetclassificationId
        });
  
        await setAssetTypeCodeField(typecode);
        setTypeCodeDescription(typeDescription);
        await setAssetTaskCodeField(bemtaskdetailsId);
        setTaskCodeDescription(taskdesc);
        setStatus(statusOptions?.find((item) => item?.default === isactive));
        setAssetClassification(
          assetClassificationOptions?.find((item) => item.id === masassetclassificationId)
        );
      }
    }
  };
  

  const saveFilesToAttachment = async (payload) => {
    try {
      if (isChanged) {
        const response = service?.label === 'FEMS' ? await axios.post(`${FemsGraph}/api/femppmchecklists`, payload, config) : await axios.post(`${BemsGraph}/api/bemppmchecklists`, payload, config);
        if (response.status >= 200 && response.status <= 204) {
          setOpenDialog(true);
          setIsChanged(false);
        }
        return response.data;
      }
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };
  const saveFilesToAttachmentUpdate = async (payload, id) => {
    try {
      if (isChanged) {
        const response = service?.label === 'FEMS' ? await axios.put(`${FemsGraph}/api/femppmchecklists/${id}`, payload, config) : await axios.put(`${BemsGraph}/api/bemppmchecklists/${id}`, payload, config);
        if (response.status >= 200 && response.status <= 204) {
          setOpenDialog(true);
          setIsChanged(false);
        }
        return response.data;
      }
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };
  function uuidv4(extension) {
    return (
      "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }) +
      "." +
      extension
    );
  }
  const registerFiles = async () => {
    let storeFileNameArray = [];
    let storeregsitredFilesResponse = [];
    let storefileUploadResponse = [];

    for (let i = 0; i < selectedFiles?.length; i++) {
      const formData = new FormData();
      const extension = selectedFiles[i]?.file?.name?.split(".").pop();
      const fileName = uuidv4(extension); // generate a unique id
      formData.append("fileObj", selectedFiles[i]?.file, fileName); // append file with unique id as filename
      storeFileNameArray.push({
        id: fileName,
        filename: `${selectedFiles[i]?.file?.name}`,
        selectedFileId: selectedFiles[i]?.id,
      });

      try {
        if (isChanged) {
          const fileUploadResponse = await axios.post(
            `${MasterGraph}/api/fileupload`,
            formData,
            config
          );
          storeregsitredFilesResponse.push(fileUploadResponse.data);
          storefileUploadResponse.push(fileUploadResponse.data);
        }
      } catch (error) {
        console.log(
          "Error uploading file:",
          error.response ? error.response.data : error.message
        );
      }
    }
    return { storefileUploadResponse, storeFileNameArray };
  };

  const fetchFiles = async () => {
    try {
      const response = (service?.label === 'FEMS' || location.state.service === 'FEMS') ? await axios.get(
        `${FemsGraph}/api/PPMChecklist/GetPPMAttachmentList/${location.state?.masassettypecodeId}/${location.state?.femtaskdetailsId}`,
        config
      ) : await axios.get(
        `${BemsGraph}/api/PPMChecklist/GetPPMAttachmentList/${location.state?.masassettypecodeId}/${location.state?.bemtaskdetailsId}`,
        config
      );
      const data = response?.data[0];
      setFRowData([{
        filename: data.documenttitle?.split(".")[0],
        size: data.filesize,
        filepath: data.filepath,
        filetype: data?.documenttitle?.split('.').pop().toLowerCase(),
        date: utilities.formatDate({
          dateString: data.createddate,
          format: "dd/mm/yyyy",
          dbdate: true,
        }),
        enableDownload: true,
        downloadId: `${data?.id}.${data?.documenttitle?.split('.').pop().toLowerCase()}`,
      }]
      );
    } catch (error) {
      console.log(
        "Error uploading file:",
        error.response ? error.response.data : error.message
      );
    }
  }
  const handleSave = async () => {
    let storefileUploadResponseFinal = [];
    const randomNumber = Math.floor(Math.random() * 1000) + 1;
    if (selectedFiles?.length > 0 && selectedCombination === false && !toAvoidDuplicateRecordInCreateMode) {
      try {
        const { storefileUploadResponse, storeFileNameArray } = await registerFiles();
        if (storefileUploadResponse && storefileUploadResponse.length > 0) {

          if (location?.state?.mode !== "edit") {
            for (let i = 0; i < storefileUploadResponse.length; i++) {
              const file = storefileUploadResponse[i];
              const documentTitle = storeFileNameArray.find((item) => item.id === file.fileName)?.filename;
              const documenttype = documentTitle?.split('.').pop().toLowerCase();
              if (documentTitle) {
                const payload = service?.label === 'FEMS' ? {
                  filename: file.fileName,
                  filesize: file.fileSize,
                  filepath: "D:/IFAC-X/UploadFolder",
                  documenttitle: documentTitle,
                  documenttype: documenttype,
                  isactive: status?.default,
                  masservicedetailsId: service?.id,
                  masassettypecodeId: typeCode?.id,
                  MasassetclassificationId: assetClassification?.id,
                  femtaskdetailsId: taskCode?.id,
                  femppmchecklistno: ppmChecklistNo,

                } : {
                  filename: file.fileName,
                  filesize: file.fileSize,
                  filepath: "D:/IFAC-X/UploadFolder",
                  documenttitle: documentTitle,
                  documenttype: documenttype,
                  isactive: status?.default,
                  masservicedetailsId: service?.id,
                  MasassetclassificationId: assetClassification?.id,
                  masassettypecodeId: typeCode?.id,
                  bemtaskdetailsId: taskCode?.id,
                  bemppmchecklistno: ppmChecklistNo,

                };

                try {
                  const response = await saveFilesToAttachment(payload);
                  setHasUnsavedChanges(false);
                  if (response) {
                    storefileUploadResponseFinal.push(response);
                    setPpmChecklistNoField(ppmChecklistNo);
                    setToAvoidDuplicateRecordInCreateMode(true);
                  } else {
                    console.log("Error saving file:", file.fileName);
                  }
                } catch (error) {
                  console.log('Error saving file:', error);
                }
              } else {
                console.log("Document Title not found for file:", file.fileName);
              }
            }
          }
          else {
            for (let i = 0; i < storefileUploadResponse.length; i++) {
              const file = storefileUploadResponse[i];
              const documentTitle = storeFileNameArray.find((item) => item.id === file.fileName)?.filename;
              const documenttype = documentTitle.split('.').pop().toLowerCase();

              if (documentTitle) {
                const payload = service?.label === 'FEMS' ? {
                  filename: file.fileName,
                  filesize: file.fileSize,
                  filepath: "D:/IFAC-X/UploadFolder",
                  documenttitle: documentTitle,
                  documenttype: documenttype,
                  isactive: status?.default,
                  masservicedetailsId: service?.id,
                  masassettypecodeId: typeCode?.id,
                  MasassetclassificationId: assetClassification?.id,
                  femtaskdetailsId: taskCode?.id,
                  femppmchecklistno: location?.state?.femppmchecklistno,
                  id: location?.state?.femppmchecklistId

                } : {
                  filename: file.fileName,
                  filesize: file.fileSize,
                  filepath: "D:/IFAC-X/UploadFolder",
                  documenttitle: documentTitle,
                  documenttype: documenttype,
                  isactive: status?.default,
                  masservicedetailsId: service?.id,
                  masassettypecodeId: typeCode?.id,
                  MasassetclassificationId: assetClassification?.id,
                  bemtaskdetailsId: taskCode?.id,
                  bemppmchecklistno: location?.state?.bemppmchecklistno,
                  id: location?.state?.bemppmchecklistId

                };

                try {
                  const response = service?.label === 'FEMS' ? await saveFilesToAttachmentUpdate(payload, location?.state?.femppmchecklistId) : await saveFilesToAttachmentUpdate(payload, location?.state?.bemppmchecklistId);
                  setHasUnsavedChanges(false);
                  if (response) {
                    storefileUploadResponseFinal.push(response);
                  } else {
                    console.log("Error saving file:", file.fileName);
                  }
                } catch (error) {
                  console.log('Error saving file:', error);
                }
              } else {
                console.log("Document Title not found for file:", file.fileName);
              }
            }

          }
          if (storefileUploadResponseFinal?.length > 0 && storefileUploadResponseFinal?.length === storefileUploadResponse?.length) {
            setOpenDialog(true);
          } else {
            console.log("Error in uploading some files");
          }
        } else {
          console.log("No files registered");
        }

      } catch (error) {
        console.log('Error registering files:', error);
      }
    }
    else if (selectedFiles?.length <= 0 && isChanged && (location?.state?.mode === "edit" || location?.state?.mode === "create") && (fRowData[0]?.filename != undefined && fRowData[0]?.filename != '')) {
      const payload = service?.label === 'FEMS' ? {
        filename: location?.state.filename,
        filesize: location?.state.filesize,
        filepath: location?.state.filepath,
        documenttitle: location?.state.documenttitle,
        documenttype: location?.state.documenttitle?.split('.').pop().toLowerCase(),
        isactive: status?.default,
        masservicedetailsId: service?.id,
        masassettypecodeId: typeCode?.id,
        MasassetclassificationId: assetClassification?.id,
        femtaskdetailsId: taskCode?.id,
        femppmchecklistno: location?.state?.femppmchecklistno,
        id: location?.state?.femppmchecklistId

      } : {
        filename: location?.state.filename,
        filesize: location?.state.filesize,
        filepath: location?.state.filepath,
        documenttitle: location?.state.documenttitle,
        documenttype: location?.state?.documenttitle?.split('.').pop().toLowerCase(),
        isactive: status?.default,
        masservicedetailsId: service?.id,
        masassettypecodeId: typeCode?.id,
        MasassetclassificationId: assetClassification?.id,
        bemtaskdetailsId: taskCode?.id,
        bemppmchecklistno: location?.state?.bemppmchecklistno,
        id: location?.state?.bemppmchecklistId
      };
      try {
        if (location?.state?.femppmchecklistId != undefined || location?.state?.bemppmchecklistId != undefined) {
          const response = service?.label === 'FEMS' ? await saveFilesToAttachmentUpdate(payload, location?.state?.femppmchecklistId) : await saveFilesToAttachmentUpdate(payload, location?.state?.bemppmchecklistId);
          setHasUnsavedChanges(false);
          if (response) {
            storefileUploadResponseFinal.push(response);
          } else {
            console.log("Error saving file:", location?.state.fileName);
          }
        } else {
          setOpenDialogForSelectedCombination(true);
        }
      } catch (error) {
        console.log('Error saving file:', error);
      }
      if (storefileUploadResponseFinal?.length > 0) {
        setOpenDialog(true);
      } else {
        console.log("Error in uploading some files");
      }
    }
    else if (selectedFiles?.length > 0 && !isDataUpdatedByChangeAttachement && isChanged && (location?.state?.mode === "edit") && (fRowData[0]?.filename != undefined && fRowData[0]?.filename != '')) {

      try {
        const { storefileUploadResponse, storeFileNameArray } = await registerFiles();
        if (storefileUploadResponse && storefileUploadResponse.length > 0) {

          if (location?.state?.mode === "edit") {
            for (let i = 0; i < storefileUploadResponse.length; i++) {
              const file = storefileUploadResponse[i];
              const documentTitle = storeFileNameArray.find((item) => item.id === file.fileName)?.filename;
              const documenttype = documentTitle?.split('.').pop().toLowerCase();
              if (documentTitle) {
                const payload = service?.label === 'FEMS' ? {
                  filename: file.fileName,
                  filesize: file.fileSize,
                  filepath: "D:/IFAC-X/UploadFolder",
                  documenttitle: documentTitle,
                  documenttype: documenttype,
                  isactive: status?.default,
                  masservicedetailsId: service?.id,
                  masassettypecodeId: typeCode?.id,
                  MasassetclassificationId: assetClassification?.id,
                  femtaskdetailsId: taskCode?.id,
                  femppmchecklistno: location?.state?.femppmchecklistno,
                  id: location?.state?.femppmchecklistId

                } : {
                  filename: file.fileName,
                  filesize: file.fileSize,
                  filepath: "D:/IFAC-X/UploadFolder",
                  documenttitle: documentTitle,
                  documenttype: documenttype,
                  isactive: status?.default,
                  masservicedetailsId: service?.id,
                  MasassetclassificationId: assetClassification?.id,
                  masassettypecodeId: typeCode?.id,
                  bemtaskdetailsId: taskCode?.id,
                  bemppmchecklistno: location?.state?.bemppmchecklistno,
                  id: location?.state?.bemppmchecklistId
                };

                if (location?.state?.femppmchecklistId != undefined || location?.state?.bemppmchecklistId != undefined) {
                  const response = service?.label === 'FEMS' ? await saveFilesToAttachmentUpdate(payload, location?.state?.femppmchecklistId) : await saveFilesToAttachmentUpdate(payload, location?.state?.bemppmchecklistId);
                  setHasUnsavedChanges(false);
                  if (response) {
                    storefileUploadResponseFinal.push(response);
                  } else {
                    console.log("Error saving file:", location?.state.fileName);
                  }
                } else {
                  setOpenDialogForSelectedCombination(true);
                }

                if (storefileUploadResponseFinal?.length > 0) {
                  setOpenDialog(true);
                } else {
                  console.log("Error in uploading some files");
                }
              }
            }
          }
        }
      } catch (error) {
        console.log('Error saving file:', error);
      }
    }
    else if (location.state?.mode === "create" && selectedCombination === true && (fRowData[0]?.filename != undefined && fRowData[0]?.filename != '') && !toAvoidDuplicateRecordInCreateMode) {
      if (`${fRowData[0]?.filename}.${fRowData[0]?.filetype.toLowerCase()}` != existImageName) {
        const payload = service?.label === 'FEMS' ? {
          filename: fRowData[0]?.filename,
          filesize: fRowData[0]?.size,
          filepath: existRow?.filepath,
          documenttitle: fRowData[0]?.filename,
          documenttype: "pdf",
          isactive: status?.default,
          masservicedetailsId: service?.id,
          masassettypecodeId: typeCode?.id,
          MasassetclassificationId: assetClassification?.id,
          femtaskdetailsId: taskCode?.id,
          femppmchecklistno: existRow?.femppmchecklistno,
          id: existRow?.femppmchecklistId

        } : {
          filename: fRowData[0]?.filename,
          filesize: fRowData[0]?.size,
          filepath: existRow?.filepath,
          documenttitle: fRowData[0]?.filename,
          documenttype: "pdf",
          isactive: status?.default,
          masservicedetailsId: service?.id,
          masassettypecodeId: typeCode?.id,
          MasassetclassificationId: assetClassification?.id,
          bemtaskdetailsId: taskCode?.id,
          bemppmchecklistno: existRow?.bemppmchecklistno,
          id: existRow?.bemppmchecklistId

        };


        try {
          const response = service?.label === 'FEMS' ? await saveFilesToAttachmentUpdate(payload, existRow?.femppmchecklistId) : await saveFilesToAttachmentUpdate(payload, existRow?.bemppmchecklistId);
          setHasUnsavedChanges(false);
          if (response) {
            storefileUploadResponseFinal.push(response);
            setToAvoidDuplicateRecordInCreateMode(true);
          } else {
            console.log("Error saving file",);
          }
        } catch (error) {
          console.log('Error saving file:', error);
        }
        if (storefileUploadResponseFinal?.length > 0) {
          setSelectedCombination(false);
          setOpenDialogForSelectedCombination(true);
          setIsDataUpdated(true);
        } else {
          console.log("Error in uploading some files");
        }
      } else {
        setOpenDialogForSelectedCombination(true);

      }
    }
    else if (fRowData[0]?.filename === undefined || fRowData[0]?.filename === '') {
      setOpenDialog(true);
    }
    else {
      setOpenDialogForSelectedCombination(true);
    }
  };
  const handleSubmit = async () => {
    const newErrors = {};
    if (!service) newErrors.service = "Service is required";
    if (!taskCode?.label) newErrors.taskCode = "Task Code is required";
    if (!typeCode?.label) newErrors.typeCode = "Asset Type Code is required";
    if (!status?.label) newErrors.status = "Status is required";
    if (!workGroup?.label) newErrors.workGroup = "Work Group is required";
    if (!assetClassification?.label) newErrors.assetClassification = "Asset Classification is required";
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
    } else {
      await handleSave();
    }
  };


  const handleCancel = () => {
    if (isChanged && !isViewMode) {
      setCancelDialogOpen(true);
    } else {
      navigate(-1, { replace: true });
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };

  const handleCancelDialogConfirm = async () => {
    if (isAddNew) {
      setCancelDialogOpen(false);
      await setDefault();
      setIsAddNew(false);
      setIsChanged(false);

    } else {
      setCancelDialogOpen(false);
      setDefault();
      setIsChanged(false);
      navigate(-1, { replace: true, prevService: location?.state?.service });
    }
  };
  const handleCloseDialog = () => {
    setOpenDialogForSelectedCombination(false);
    setOpenDialog(false);
    setIsAddNew(false);
    setIsChanged(false);

  };
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  return (
    <>
      <PageTitle
        title={"Create PPM Check List"}
        isEdit={location.state.mode === "edit"}
        isView={location.state.mode === "view"}
        editTitle={"Edit PPM Check List"}
        viewTitle={"View PPM Check List"}
      />
      <DefaultCard sx={{ height: '100%' }}>
        <form style={{ position: 'relative' }}>
          <Box mb={2}>
            <Grid container spacing={2} marginBottom={'60px'}>
              <Grid item xs={6} md={4}>
                <CustomTextField
                  maxlength={225}
                  label="PPM Checklist No.(Auto)"
                  id="ppmChecklistNo"
                  value={ppmChecklistNoField}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomSelectInput
                  id="service-label"
                  label="Service"
                  value={service}
                  options={serviceOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={
                    (newValue) => {
                      setService(newValue);
                      if (newValue != undefined) {
                        setErrors((prevErrors) => ({ ...prevErrors, service: "" }));
                        setIsChanged(true);
                        setToAvoidDuplicateRecordInCreateMode(false);
                      } else if (newValue == undefined) {
                        setWorkGroup({ label: '', value: '' });
                        setWorkGroupOptions([]);
                      }
                    }
                  }
                  disabled={location.state.mode === "view" || location.state.mode === "edit"}
                  isMandatoryField={true}
                  error={!!errors.service}
                  errorMessage={errors.service}
                  autoFocus
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomSelectInput
                  id="work-group-label"
                  label="Work Group"
                  isMandatoryField={true}
                  value={workGroup}
                  errorMessage={errors.workGroup}
                  error={!!errors.workGroup}
                  options={workGroupOptions}
                  onChange={(newValue) => {
                    setErrors({ ...errors, workGroup: "" });
                    setWorkGroup(newValue);
                    if (newValue?.label != '') {
                      setTaskCode({ label: '', value: '' });
                      setTaskCodeDescription('');
                    }
                  }}
                  disabled={location.state.mode === "view" || location.state.mode === "edit"}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomSelectInput
                  id="assetclassification"
                  label="Asset Classification"
                  value={assetClassification}
                  error={!!errors?.assetClassification}
                  errorMessage={errors.assetClassification}
                  options={assetClassificationOptions?.sort((a, b) => a.label.localeCompare(b.label))}
                  onChange={(newValue) => {
                    setAssetClassification(newValue);
                    if (newValue?.value === '') {
                      setAssetClassification('');
                      setTypeCode({ label: '', value: '' })
                      setTypeCodeDescription("")
                      setTaskCode({ label: '', value: '' })
                      setTaskCodeDescription("")
                    }
                    if (newValue?.value !== '') {
                      setErrors((prevErrors) => ({ ...prevErrors, assetClassification: "" }));
                      setIsChanged(true);
                      setToAvoidDuplicateRecordInCreateMode(false);
                      setTypeCode({id:"",label:""})
                      setTypeCodeDescription("")
                      setTaskCode({id:"",label:""})
                      setTaskCodeDescription("")
                    }
                  }}
                  disabled={location.state.mode === "view"}
                  isMandatoryField={true}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomTextFieldWithSearch
                  label="Asset Type Code"
                  id="asset-typecode"
                  value={typeCode}
                  options={typeCodeOptions?.map(option => ({
                    label: `${option.label} - ${option.typedescription}`,
                    value: option.value,
                    typedescription: option?.typedescription,
                    id: option?.id
                  }))?.sort((a, b) => a.label.localeCompare(b.label))
                  }
                  onChange={handleTypeCodeChange}
                  disabled={location.state.mode === "view"}
                  isMandatoryField={true}
                  error={errors.typeCode}
                  errorMessage={errors.typeCode}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomTextField
                  maxlength={225}
                  label="Asset Type Code Description"
                  id="asset-typecode-desc"
                  value={typeCodeDescription}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={(location.state.mode == "view") ? 12 : 11.5} md={(location.state.mode == "view") ? 4 : 3.5} sm={(location.state.mode == "view") ? 6 : 5.5} >
              <CustomTextFieldWithSearch
                  label="Task Code"
                  id="asset-taskcode"
                  value={taskCode}
                  options={taskCodeOptions?.map(option => ({
                    label: `${option.label} - ${option.taskdesc}`,
                    value: option.value,
                    taskdesc: option?.taskdesc,
                    id: option?.id
                  }))?.sort((a, b) => a.label.localeCompare(b.label))}
                  onChange={handleTaskCodeChange}
                  disabled={location.state.mode === "view"}
                  isMandatoryField={true}
                  error={!!errors.taskCode}
                  errorMessage={errors.taskCode}
                />
            </Grid>

            {(location.state.mode != "view") && (<Grid item xs={0.5} md={0.5} sm={0.5} >
              {location.state.mode != "view" && (
                <Grid sx={{ mt: 2.7 }} item>
                  <Button
                    onClick={handleOpenModal}
                    variant="contained"
                    sx={{ minWidth: '32px', minHeight: '32px', marginLeft: '0rem' }}
                    disabled={location.state.mode == "view"}>
                    <SearchIcon fontSize="small" />
                  </Button>
                </Grid>
              )}

              <Dialog
                open={isModalOpen}
                onClose={(event, reason) => {
                  if (reason === "backdropClick") {
                    return; // Prevent closing on backdrop click
                  }
                  handleCloseModal(); // Call the close function for other reasons
                }}
                aria-labelledby="asset-grid-dialog"
                aria-describedby="grid-display-for-assets"
                maxWidth="lg"
                fullWidth
              ><DialogTitle
                id="asset-grid-dialog"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.5rem', // Adjust font size as desired
                  display: 'flex',
                  justifyContent: 'space-between', // Space out title and icon
                  alignItems: 'center',
                }}
              >
                  Task Code Search
                  <IconButton
                    aria-label="close"
                    variant="outlined"
                    onClick={handleCloseModal}
                    sx={{
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent
                  dividers
                  sx={{
                    overflowY: 'auto', // Enables scroll if content overflows
                    height: 'auto',    // Allows height to adjust based on content
                    maxHeight: 'auto', // Sets a maximum height to prevent it from growing too large
                  }}
                >
                  <CustomAgGrid
                    rows={taskCodeOptions || []}
                    columns={columnsTaskCode}
                    // loading={loadingAssets}
                    refSet={gridAPI}
                    setGridApi={setGridAPI}
                  />
                  <Stack my={3} direction={"row"} justifyContent={"end"}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                      Close
                    </Button>
                  </Stack>
                </DialogContent>
              </Dialog>

            </Grid>)}
              <Grid item xs={6} md={4}>
                <CustomTextField
                  maxlength={255}
                  label="Task Code Description"
                  id="task-code-description"
                  value={taskCodeDescription}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomSelectInput
                  id="status"
                  label="Status"
                  value={status}
                  error={!!errors?.status}
                  errorMessage={errors.status}
                  options={statusOptions}
                  onChange={(newValue) => {
                    setStatus(newValue);
                    if (newValue?.value === '') {
                      setStatus('');
                    }
                    if (newValue?.value !== '') {
                      setErrors((prevErrors) => ({ ...prevErrors, status: "" }));
                      setIsChanged(true);
                    }
                  }}
                  disabled={location.state.mode === "view"}
                  isMandatoryField={true}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </DefaultCard>
      <FileHandling
        ref={childRef}
        isEdit={location.state.mode === "edit"}
        id={id}
        rowData={fRowData}
        setRowData={setFRowData}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        service={location.state.service}
        isView={location.state.mode === "view"}
        fetchFiles={fetchFiles}
        locationState={location?.state}
        taskCode={taskCode}
        typeCode={typeCode}
        selectedCombination={selectedCombination}
        setSelectedCombination={setSelectedCombination}
      />
      <ActionButtons
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        handleAddNew={handleAddNew}
        enableSubmit={enableSubmit}
        isEdit={location.state.mode === "edit"}
        isView={location.state.mode === "view"}
      />

      <CustomDialog
        open={openDialog}
        onClose={handleCloseDialog}
        content={((selectedFiles?.length === 0 || fRowData[0]?.filename === undefined || fRowData[0]?.filename === '') && location?.state?.mode != "edit") || (location?.state.mode === "edit" && fRowData[0]?.filename === "") ? "No files uploaded in the attachment." : location?.state.mode !== "edit" ? "Data saved successfully." : "Data updated successfully."}
        confirmBtn={"OK"}
        onConfirm={handleCloseDialog}
      />
      <CustomDialog
        open={openDialogForSelectedCombination}
        onClose={handleCloseDialog}
        content={(!selectedCombination && isDataUpdated) ? "Data updated successfully." : "PPM Checklist for the selected combination is already exist."}
        confirmBtn={"OK"}
        onConfirm={handleCloseDialog}
      />
      <CustomDialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        title="Unsaved Changes"
        content="Record(s) not Saved/Updated. Do you want to continue?"
        confirmBtn="Yes"
        cancelBtn="No"
        onConfirm={handleCancelDialogConfirm}
        onCancel={handleCancelDialogClose}
      />
    </>
  );
}






